import React from 'react';

import dayjs from 'dayjs';
import { getResourceValidate } from 'services/resources';
import * as Yup from 'yup';

import { ResourceTypeRules, FieldName } from '../config/resourceTypeRules';
import { ENTRY_TYPE } from '../index';

interface BuildResourceSubmissionSchemaParams {
  entryType: string | null;
  rules: ResourceTypeRules;
  existingResource?: any;
  bestOfYear?: number;
  testedTitles: React.MutableRefObject<string[]>;
  invalidTitles: React.MutableRefObject<string[]>;
  testedLinks: React.MutableRefObject<string[]>;
  invalidLinks: React.MutableRefObject<string[]>;
  invalidValues: { [key: string]: { value: string; message: string }[] };
  lastValidatedTitle: React.MutableRefObject<string | null>;
  lastValidatedLink: React.MutableRefObject<string | null>;
}

const serverStringValidation = (
  value: string,
  ctx: Yup.TestContext<Yup.AnyObject>,
  invalidValues: { [key: string]: { value: string; message: string }[] }
) => {
  if (!invalidValues[ctx.path]) return true;

  const invalid = invalidValues[ctx.path].find((invalidValue) => invalidValue.value === value);

  if (invalid) {
    return ctx.createError({
      path: ctx.path,
      message: invalid.message,
    });
  }

  return true;
};

export const buildResourceSubmissionSchema = ({
  entryType,
  rules,
  existingResource,
  bestOfYear,
  testedTitles,
  invalidTitles,
  testedLinks,
  invalidLinks,
  invalidValues,
  lastValidatedTitle,
  lastValidatedLink,
}: BuildResourceSubmissionSchemaParams) => {
  const currentResourceFormat = entryType?.toLowerCase();

  // Helper to conditionally apply required based on resource_type rules
  // If rules specify required=true, make it required
  // If rules specify required=false, make it not required (and remove min constraints)
  // If rules don't specify (undefined), keep base schema behavior
  const applyResourceTypeRequiredness = (
    fieldName: FieldName,
    baseSchema: Yup.AnySchema
  ): Yup.AnySchema => {
    const requiredByRules = rules.required?.[fieldName];
    if (requiredByRules === true) {
      return (baseSchema as any).required('Required');
    }
    if (requiredByRules === false) {
      // When not required, we need to remove min constraints for arrays
      // For arrays, rebuild without min constraint to allow empty arrays
      if ((baseSchema as any)._type === 'array') {
        return Yup.array().notRequired().nullable();
      }
      // For other types, just make it not required
      return (baseSchema as any).notRequired().nullable();
    }
    // undefined: keep base schema behavior
    return baseSchema;
  };

  // Build schema with all resource_format_uuid logic intact, and layer resource_type requiredness
  return Yup.object().shape({
    title: applyResourceTypeRequiredness(
      'title',
      Yup.string()
        .test('server-validation', 'Invalid', (value, ctx) =>
          serverStringValidation(value, ctx, invalidValues)
        )
        .test('title-remote', 'This title is already taken.', async function (value, ctx) {
          if (!value) {
            lastValidatedTitle.current = null;
            return true;
          }

          // Skip validation if the value hasn't changed from the last validated value
          if (lastValidatedTitle.current === value) return true;

          // Early return if already tested - prevents unnecessary API calls
          if (testedTitles.current.includes(value)) {
            lastValidatedTitle.current = value;
            return true;
          }
          if (invalidTitles.current.includes(value)) {
            ctx.createError({ path: ctx.path, message: 'This title is already taken.' });
            return false;
          }

          try {
            const res = await getResourceValidate(
              existingResource
                ? { title: value, resource_uuid: existingResource.uuid }
                : { title: value }
            );

            if (res?.data.valid) {
              testedTitles.current.push(value);
              lastValidatedTitle.current = value;
              return true;
            }
            ctx.createError({ path: ctx.path, message: res?.data?.errors['title'] });
          } catch (err) {
            ctx.createError({
              path: ctx.path,
              message: 'Could not validate title with server',
            });
          }
        })
        .required('Required') // Base behavior: required
    ),
    resource_submission_category_uuid: bestOfYear
      ? Yup.string()
          .test('server-validation', 'Invalid', (value, ctx) =>
            serverStringValidation(value, ctx, invalidValues)
          )
          .required('Required')
      : applyResourceTypeRequiredness(
          'resource_submission_category_uuid',
          Yup.string()
            .test('server-validation', 'Invalid', (value, ctx) =>
              serverStringValidation(value, ctx, invalidValues)
            )
            .notRequired()
        ),
    resource_format_uuid: Yup.string()
      .required('Required')
      .test('server-validation', 'Invalid', (value, ctx) =>
        serverStringValidation(value, ctx, invalidValues)
      ),
    link: Yup.string()
      .when('resource_format_uuid', {
        is: () => currentResourceFormat === ENTRY_TYPE.VIMEO_YOUTUBE,
        then: (schema) =>
          schema
            .matches(
              /^(https?:\/\/)?(www\.)?(youtube\.com\/embed\/[a-zA-Z0-9_-]+(\?.*)?|player\.vimeo\.com\/video\/[0-9]+(\?.*)?)$/,
              `That doesn't look like an embed link. Vimeo embed links can be found by clicking into the video's submenu and clicking Copy Embed code - it will look like this: https://player.vimeo.com/video/1027751244. Youtube embed links can be found by clicking Share > Embed - it will look like this: https://www.youtube.com/embed/XF3M80zUaZU`
            )
            .required('Required'),
      })
      .when('resource_format_uuid', {
        is: () => currentResourceFormat === ENTRY_TYPE.LINK,
        then: (schema) =>
          schema
            .matches(
              /(https?:\/\/(?:www\.)?([a-zA-Z0-9_-]+\.)+[a-zA-Z0-9_-]+(\/[a-zA-Z0-9_-]+)*)/,
              "That doesn't look right. URLs should start with https://www.... "
            )
            .test(
              'link-remote',
              'This link is already set in another resource.',
              async function (value, ctx) {
                if (!value) {
                  lastValidatedLink.current = null;
                  return true;
                }

                // Skip validation if the value hasn't changed from the last validated value
                if (lastValidatedLink.current === value) return true;

                if (testedLinks.current.includes(value)) {
                  lastValidatedLink.current = value;
                  return true;
                }
                if (invalidLinks.current.includes(value)) {
                  ctx.createError({
                    path: ctx.path,
                    message: 'This link is already set in another resource.',
                  });
                  return false;
                }

                try {
                  const res = await getResourceValidate(
                    existingResource
                      ? { url: value, resource_uuid: existingResource.uuid }
                      : { url: value }
                  );
                  if (res?.data.valid) {
                    testedLinks.current.push(value);
                    lastValidatedLink.current = value;
                    return true;
                  }

                  ctx.createError({ path: ctx.path, message: res?.data?.errors['url'] });
                } catch (err) {
                  ctx.createError({
                    path: ctx.path,
                    message: 'Could not validate link with server',
                  });
                }
              }
            )
            .required('Required'),
      })
      .when('resource_format_uuid', {
        is: () => currentResourceFormat === ENTRY_TYPE.READYMAG,
        then: (schema) =>
          schema
            .matches(
              /(https?:\/\/(?:www\.)?readymag\.com\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+\/?)/,
              "That doesn't look right. It should be formatted as https://readymag.com/designs/lumina/"
            )
            .required('Required'),
      })
      .when('resource_format_uuid', {
        is: () => currentResourceFormat === ENTRY_TYPE.GOOGLE_SLIDES,
        then: (schema) =>
          schema
            .matches(
              /^https:\/\/docs\.google\.com\/presentation\/d\/e\/[a-zA-Z0-9_-]+\/pubembed(\?.*)?$|^https:\/\/[^\/]+\/embeddable_resource\?slug=/,
              "That doesn't look right. The correct Google Slide URL format is 'https://docs.google.com/presentation/d/e/2PACX-1vS-zD0zY7ig77k5z0qCgg4Ur1BdIJiG5mNlkTK3fov0udRhoDtE3G9kWGwu6DEheuv2f0cvnxxcUaux/pubembed?start=false&loop=false&delayms=3000'.\n\nIn Google Slides, navigate to File > Share > Publish to web > Embed and click publish to find the correct URL."
            )
            .required('Required'),
      })
      .when('resource_format_uuid', {
        is: () => currentResourceFormat === ENTRY_TYPE.GOOGLE_STUDIO,
        then: (schema) =>
          schema
            .matches(
              /^https:\/\/lookerstudio\.google\.com\/reporting\/[a-zA-Z0-9_-]+$/,
              "That doesn't look right.  It should be formatted as https://lookerstudio.google.com/reporting/f1837c75-6dd4-4355-9966-ca1785d14302"
            )
            .required('Required'),
      })
      .when('resource_format_uuid', {
        is: () => currentResourceFormat === ENTRY_TYPE.KYU_OS,
        then: (schema) =>
          schema
            .matches(
              /^https:\/\/os\.kyu\.com\/.*/,
              "That doesn't look right.  It should be formatted as https://os.kyu.com/....."
            )
            .required('Required'),
      })
      .when('resource_format_uuid', {
        is: () =>
          ![
            ENTRY_TYPE.VIMEO_YOUTUBE,
            ENTRY_TYPE.READYMAG,
            ENTRY_TYPE.LINK,
            ENTRY_TYPE.GOOGLE_SLIDES,
            ENTRY_TYPE.GOOGLE_STUDIO,
          ].includes(currentResourceFormat),
        then: (schema) => schema.notRequired(),
      })
      .test('server-validation', 'Invalid', (value, ctx) =>
        serverStringValidation(value, ctx, invalidValues)
      ),
    short_description: applyResourceTypeRequiredness(
      'short_description',
      Yup.string()
        .test('server-validation', 'Invalid', (value, ctx) =>
          serverStringValidation(value, ctx, invalidValues)
        )
        .required('Required') // Base behavior: required
    ),
    long_description: applyResourceTypeRequiredness(
      'long_description',
      Yup.string()
        .test('server-validation', 'Invalid', (value, ctx) =>
          serverStringValidation(value, ctx, invalidValues)
        )
        .required('Required') // Base behavior: required
    ),
    tags: Yup.array().notRequired(),
    file: Yup.array().when('resource_format_uuid', {
      is: () => [ENTRY_TYPE.PDF, ENTRY_TYPE.IMAGE].includes(currentResourceFormat),
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    image: applyResourceTypeRequiredness(
      'image',
      Yup.array().min(1, 'At least 1 image is required').required('Required') // Base behavior: required
    ),
    contributors_uuids: applyResourceTypeRequiredness(
      'contributors_uuids',
      Yup.array().min(1, 'At least 1 contributor is required') // Base behavior: required (via min)
    ),
    companies_uuids: applyResourceTypeRequiredness(
      'companies_uuids',
      Yup.array().min(1, 'At least 1 company is required') // Base behavior: required (via min)
    ),
    pulse_submission: Yup.boolean(),
    internal_initiative: Yup.boolean(),
    clients_uuids: Yup.array().when('internal_initiative', {
      is: (internal_initiative) => !internal_initiative,
      then: (schema) =>
        applyResourceTypeRequiredness(
          'clients_uuids',
          schema.min(1, 'At least 1').max(1, 'Maximum one client').required('Required') // Base behavior: required when not internal
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    project_categories_uuids: applyResourceTypeRequiredness(
      'project_categories_uuids',
      Yup.array().min(1, 'At least 1').required('Required') // Base behavior: required
    ),
    started_at: applyResourceTypeRequiredness(
      'started_at',
      Yup.date().required('Required') // Base behavior: required
    ),
    released_at: Yup.date()
      .notRequired()
      .test(
        'released-at-greater-than-started-at',
        'End date must be greater than start date',
        (value, ctx) => {
          if (!ctx.parent.started_at || !value) return true;
          return dayjs(value).isAfter(dayjs(ctx.parent.started_at));
        }
      ),
    additional_images: Yup.array().when('resource_format_uuid', {
      is: () => currentResourceFormat === ENTRY_TYPE.IMAGE,
      then: (schema) => schema.min(1, 'At least 1').required('Required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    internal_share_status: (() => {
      const requiredByRules = rules.required?.['internal_share_status'];
      if (requiredByRules === false) {
        // When not required, allow undefined/null OR 'open' OR 'blinded'
        // Use a test to only validate when a value is provided
        return Yup.string()
          .test('optional-oneOf', 'Must be "open" or "blinded" if provided', function (value) {
            // If value is undefined or null, it's valid (field is optional)
            if (value === undefined || value === null) return true;
            // If value is provided, it must be 'open' or 'blinded'
            return value === 'open' || value === 'blinded';
          })
          .notRequired()
          .nullable();
      }
      // When required or undefined, use base behavior (only 'open' allowed)
      return Yup.string().oneOf(['open'], 'Required').required('Required');
    })(),
    external_client_share_status: applyResourceTypeRequiredness(
      'external_client_share_status',
      Yup.string().required('Required') // Base behavior: required
    ),
    skills_uuids: applyResourceTypeRequiredness(
      'skills_uuids',
      Yup.array().min(1, 'At least 1').required('Required') // Base behavior: required
    ),
    impact_metrics: Yup.string().notRequired(),
    draft: Yup.boolean().notRequired(),
  });
};
